export const truncateMarkdown = (markdown, maxLength = 1000) => {
  if (!markdown || markdown.length <= maxLength) {
    return markdown;
  }

  let breakPoint = maxLength;
  const lastSentence = markdown.lastIndexOf(".", maxLength);
  const lastParagraph = markdown.lastIndexOf("\n\n", maxLength);
  const lastLine = markdown.lastIndexOf("\n", maxLength);

  if (lastSentence > maxLength * 0.8) {
    breakPoint = lastSentence + 1;
  } else if (lastParagraph > maxLength * 0.7) {
    breakPoint = lastParagraph;
  } else if (lastLine > maxLength * 0.6) {
    breakPoint = lastLine;
  }

  return markdown.substring(0, breakPoint) + "\n\n...";
};

export const estimateReadingTime = (markdown) => {
  if (!markdown) return 0;

  const plainText = markdown
    .replace(/#{1,6}\s+/g, "") // Remove headers
    .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold
    .replace(/\*(.*?)\*/g, "$1") // Remove italic
    .replace(/`(.*?)`/g, "$1") // Remove inline code
    .replace(/```[\s\S]*?```/g, "") // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Remove links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "") // Remove images
    .replace(/\n+/g, " ") // Replace newlines with spaces
    .trim();

  const wordCount = plainText
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
  const wordsPerMinute = 200; // Average reading speed

  return Math.ceil(wordCount / wordsPerMinute);
};

export const extractTableOfContents = (markdown) => {
  if (!markdown) return [];

  const toc = [];
  const lines = markdown.split("\n");

  lines.forEach((line, index) => {
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      const anchor = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();

      toc.push({
        level,
        text,
        anchor,
        lineNumber: index,
      });
    }
  });

  return toc;
};

export const validateMarkdown = (markdown) => {
  const issues = [];
  const suggestions = [];

  if (!markdown) {
    return { issues, suggestions, isValid: true };
  }

  // Check for very long lines (potential table issues)
  const lines = markdown.split("\n");
  lines.forEach((line, index) => {
    if (line.length > 120) {
      issues.push({
        type: "long-line",
        line: index + 1,
        message: `Line ${index + 1} is very long (${line.length} characters)`,
      });
      suggestions.push(
        "Consider breaking long lines or using tables for structured data"
      );
    }
  });

  // Check for unclosed code blocks
  const codeBlockMatches = markdown.match(/```/g);
  if (codeBlockMatches && codeBlockMatches.length % 2 !== 0) {
    issues.push({
      type: "unclosed-code-block",
      message: "Unclosed code block detected",
    });
    suggestions.push("Ensure all code blocks are properly closed with ```");
  }

  // Check for malformed links
  const linkMatches = markdown.match(/\[([^\]]*)\]\(([^)]*)\)/g);
  if (linkMatches) {
    linkMatches.forEach((link, index) => {
      const match = link.match(/\[([^\]]*)\]\(([^)]*)\)/);
      if (match && (!match[1] || !match[2])) {
        issues.push({
          type: "malformed-link",
          message: `Malformed link at position ${index}: ${link}`,
        });
        suggestions.push("Ensure links have both text and URL: [text](url)");
      }
    });
  }

  return {
    issues,
    suggestions,
    isValid: issues.length === 0,
  };
};

export const optimizeForStreaming = (markdown) => {
  if (!markdown) return "";

  // Remove excessive whitespace
  let optimized = markdown
    .replace(/\n{3,}/g, "\n\n") // Replace 3+ newlines with 2
    .replace(/[ \t]+$/gm, "") // Remove trailing spaces
    .trim();

  // Ensure proper spacing around headers
  optimized = optimized.replace(/(\n|^)(#{1,6}\s+)/g, "\n\n$2");

  return optimized;
};

// Attempts to fix common minor markdown mistakes so rendering is robust.
// - Closes unmatched ** and * at the end of the text
// - Ensures code fences are balanced by appending closing ``` if needed
export const normalizeMarkdown = (markdown) => {
  if (!markdown) return "";
  let normalized = markdown;

  // Balance code fences ```
  const fenceMatches = normalized.match(/```/g);
  if (fenceMatches && fenceMatches.length % 2 !== 0) {
    normalized += "\n```";
  }

  // Balance bold markers ** (count all occurrences of ** that are not part of ``` blocks)
  const doubleAsteriskCount = (normalized.match(/\*\*/g) || []).length;
  if (doubleAsteriskCount % 2 !== 0) {
    normalized += " **";
  }

  // Balance single * for italics after removing the double matches to avoid double counting
  const withoutDouble = normalized.replace(/\*\*/g, "");
  const singleAsteriskCount = (withoutDouble.match(/\*/g) || []).length;
  if (singleAsteriskCount % 2 !== 0) {
    normalized += " *";
  }

  return normalized;
};
